import React, { useState, useEffect } from 'react';
import Loader from 'components/Loader';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/solid';
import CoverArtImg from 'components/CoverArtImg';
import { elipsify } from 'api/util';
import clsx from 'clsx';

const Paginator = ({ setPage, totalCount, page, postsPerPage }) => {
  // Compute the number of pages.
  const numPages = Math.ceil(totalCount / postsPerPage);

  // Create an array of page numbers to display.
  const pages = [];
  for (let i = 1; i <= numPages; i++) {
    // Show first 5 pages, last page, or pages close to the current page.
    if (i <= 5 || i === numPages || Math.abs(page - i) <= 1) {
      pages.push(i);
    }
  }

  const isFirstPage = page <= 1;
  const isLastPage = page >= numPages;

  const paginateForward = () => {
    if (!isLastPage) {
      setPage(page + 1);
    }
  };

  const paginateBack = () => {
    if (!isFirstPage) {
      setPage(page - 1);
    }
  };

  return (
    <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
      {/* Mobile Controls */}
      <div className="flex-1 flex justify-between sm:hidden">
        <button
          onClick={paginateBack}
          disabled={isFirstPage}
          className={clsx(
            'relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white',
            {
              'hover:bg-gray-50': !isFirstPage,
              'cursor-not-allowed bg-gray-200': isFirstPage,
            }
          )}
        >
          Previous
        </button>
        <button
          onClick={paginateForward}
          disabled={isLastPage}
          className={clsx(
            'ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white',
            {
              'hover:bg-gray-50': !isLastPage,
              'cursor-not-allowed bg-gray-200': isLastPage,
            }
          )}
        >
          Next
        </button>
      </div>

      {/* Desktop Controls */}
      <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-700">
            Showing{' '}
            <span className="font-medium">
              {page * postsPerPage - postsPerPage + 1}
            </span>{' '}
            to{' '}
            <span className="font-medium">
              {page * postsPerPage > totalCount
                ? totalCount
                : page * postsPerPage}
            </span>{' '}
            of <span className="font-medium">{totalCount}</span> results
          </p>
        </div>
        <div>
          <nav
            className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px mr-8 mb-2"
            aria-label="Pagination"
          >
            {/* Previous Button */}
            <button
              onClick={paginateBack}
              disabled={isFirstPage}
              className={clsx(
                'relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500',
                {
                  'hover:bg-gray-200 cursor-pointer': !isFirstPage,
                  'bg-gray-200 cursor-not-allowed': isFirstPage,
                }
              )}
            >
              <span className="sr-only">Previous</span>
              <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
            </button>

            {/* Page Number Buttons */}
            {pages.map((pg) => (
              <button
                key={pg}
                aria-current={page === pg ? 'page' : undefined}
                disabled={page === pg}
                onClick={() => setPage(pg)}
                className={clsx(
                  'z-10 relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                  {
                    'border-2 bg-indigo-50 border-indigo-500 text-indigo-600': page === pg,
                    'border-gray-300 text-gray-500 hover:bg-gray-200': page !== pg,
                  }
                )}
              >
                {pg}
              </button>
            ))}

            {/* Next Button */}
            <button
              onClick={paginateForward}
              disabled={isLastPage}
              className={clsx(
                'relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500',
                {
                  'hover:bg-gray-200 cursor-pointer': !isLastPage,
                  'bg-gray-200 cursor-not-allowed': isLastPage,
                }
              )}
            >
              <span className="sr-only">Next</span>
              <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
};

export function MusicChart({
  list,
  limit = 20, // can be a number or 'full'
  loading = false,
  include_count = false,
  report = false,
  ordered_chart = false,
}) {
  // Manage postsPerPage and page locally.
  const [postsPerPage, setPostsPerPage] = useState(limit);
  const [page, setPage] = useState(1);

  // When the limit changes, update postsPerPage and reset to the first page.
  useEffect(() => {
    setPostsPerPage(limit === 'full' ? 100 : limit);
    setPage(1);
  }, [limit]);

  if(!Array.isArray(list)){
    list = [];
  }
  // Use the full list length as totalCount.
  const totalCount = list.length;
  const numPages = Math.ceil(totalCount / postsPerPage);

  // Slice the list to only display records for the current page.
  const displayedList = list.slice(
    (page - 1) * postsPerPage,
    page * postsPerPage
  );

  // For numbering each record.
  const getTitlePrefix = (i) => {
    return limit === 'full'
      ? (page - 1) * postsPerPage + i + 1 + '. '
      : i + 1 + '. ';
  };

  return (
    <div className="rounded shadow">
      {loading ? (
        <Loader upload_message="loading" full_height={true} />
      ) : (
        <table className="min-w-full divide-y divide-gray-100">
          <thead className="bg-gray-50">
            <tr>
              {include_count && (
                <th
                  scope="col"
                  className="py-3.5 pl-4 pr-3 text-left text-sm font-medium text-gray-900 sm:pl-6"
                >
                  No.
                </th>
              )}
              <th
                scope="col"
                className="py-3.5 pl-4 pr-3 text-left text-sm font-medium text-gray-900 sm:pl-6"
              >
                {ordered_chart ? 'Name' : 'Title'}
              </th>
              {!ordered_chart && (
                <>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-sm font-medium text-gray-900"
                  >
                    Artist
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-sm font-medium text-gray-900"
                  >
                    Album
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-sm font-medium text-gray-900"
                  >
                    Label
                  </th>
                </>
              )}
              {report && (
                <>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-sm font-medium text-gray-900"
                  >
                    Count
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-left text-sm font-medium text-gray-900"
                  >
                    Duration
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {displayedList.map((item, i) => (
              <tr
                key={item.title + i}
                className="hover:bg-gray-50 font-light text-gray-600"
              >
                {include_count && (
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                    {(page - 1) * postsPerPage + i + 1}
                  </td>
                )}
                {ordered_chart ? (
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    {item?.artist ?? item?.label}
                  </td>
                ) : (
                  <>
                    <td
                      className="whitespace-nowrap py-2 pl-4 pr-3 text-sm sm:pl-6"
                      title={item?.title}
                    >
                      <CoverArtImg title={item?.title} artist={item?.artist} />
                      {elipsify(getTitlePrefix(i) + item?.title, 40)}
                    </td>
                    {[item?.artist, item?.album, item?.label].map(
                      (displayTxt, displayTxtIndex) => (
                        <td
                          key={displayTxt + displayTxtIndex}
                          className="whitespace-nowrap px-3 py-4 text-sm"
                        >
                          {elipsify(displayTxt, 20)}
                        </td>
                      )
                    )}
                  </>
                )}
                {report && (
                  <>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      {item?.play_count || item?.count}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      {item?.play_duration}
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {!loading && totalCount === 0 && (
        <div className="h-screen text-center overflow-hidden">
          No results
        </div>
      )}
      <Paginator
        setPage={setPage}
        totalCount={totalCount}
        page={page}
        postsPerPage={postsPerPage}
      />
    </div>
  );
}
